import { millisecondsToHours } from "date-fns";

import { findActiveModules } from "tools/helpers";
import { getStorage } from "store/local";
import { isChangeShiftWithinLimit } from "./helper";
import PermissionsChecklist from "./PermissionsChecklist";

const getTotalTime = (name, record) => {};

function readOnlyCommon(record) {
  const now = new Date();
  const ms = now - record?.enter_timestamp;
  if (millisecondsToHours(ms) > 18) {
    return true;
  }
  return record?.state?.id === "done";
}

function readOnlyByAttendance(record, nameField) {
  if (nameField === "shift_kind") {
    const staffConfig = getStorage("staffConfig");
    if (
      record?.shift_date &&
      !isChangeShiftWithinLimit(record.shift_date, staffConfig?.change_shift_limit)
    ) {
      return true;
    }

    const att = record?.attendance;
    if (att && att instanceof Map && att.size > 0) {
      const access_ = att.values().next().value;
      const stateVal =
        typeof access_?.state === "object"
          ? access_?.state?.id
          : access_?.state;
      if (["done", "approved", "not_approved"].includes(stateVal)) {
        return true;
      }
    }
    return false;
  }

  const res = readOnlyCommon(record);
  if (res) return true;

  const att = record?.attendance;
  if (att && att instanceof Map && att.size > 0) {
    const access_ = att.values().next().value;
    if (nameField === "exit_timestamp" && access_.exit_timestamp) {
      return true;
    } else if (nameField === "enter_timestamp" && access_.enter_timestamp) {
      return true;
    }
  }
  return false;
}

function invisibleDayToCompensate(_, record) {
  return record?.shift_kind?.mode !== "compensatory";
}

function layoutAttendance() {
  return {
    model: "staff.access",
    target: "shift",
    form_action: [],
    table_action: [],
    fields: {
      enter_timestamp: {
        type: "datetime",
        required: true,
      },
      exit_timestamp: {
        type: "datetime",
      },
      enter_biometric: {
        type: "datetime",
        readOnly: true,
      },
      exit_biometric: {
        type: "datetime",
        readOnly: true,
      },
      state: {
        type: "selection",
        readOnly: true,
        default: "open",
        options: [
          { id: "open", name: "Abierto" },
          { id: "close", name: "Cerrado" },
          { id: "done", name: "Finalizado" },
        ],
      },
      notes: { type: "text" },
      enter_permission: {
        type: "selection",
        options: [
          { id: "", name: "" },
          { id: "late", name: "Tarde" },
          { id: "early", name: "Temprano" },
          { id: "late_justified", name: "Tarde Justificado" },
        ],
      },
      exit_permission: {
        type: "selection",
        options: [
          { id: "", name: "" },
          { id: "late", name: "Tarde" },
          { id: "early", name: "Temprano" },
          { id: "late_justified", name: "Tarde Justificado" },
        ],
      },
    },
    list: [
      { name: "enter_timestamp" },
      { name: "exit_timestamp" },
      { name: "state" },
    ],
    form: [],
  };
}

const OPTIONS = [
  { id: "late", name: "Tarde" },
  { id: "early", name: "Temprano" },
  { id: "late_justified", name: "Tarde Justificado" },
];

const IS_BILLABLE = [
  { id: "si", name: "Si" },
  { id: "no", name: "No" },
];

const STATES = [
  { id: "draft", name: "Borrador" },
  { id: "confirmed", name: "Confirmado" },
  { id: "done", name: "Finalizado" },
  { id: "cancelled", name: "Cancelado" },
];

const extraDepends = ["staff_access_bill", "crm_staff", "staff_access_extratime"];

async function layoutShift() {
  const res = await findActiveModules(extraDepends);
  const ctxView = {
    model: "staff.shift",
    form_action: [],
    extraModules: res,
    selectable: "multi", // options: multi - one - false
    fields: {
      notes: { type: "text", readOnly: readOnlyCommon },
      shift_kind: {
        type: "many2one",
        model: "staff.shift.kind",
        readOnly: (rec) => readOnlyByAttendance(rec, "shift_kind"),
        required: true,
        preloaded: true,
        attrs: ["code", "name", "mode"],
      },
      shift_date: {
        type: "date",
        readOnly: true,
        required: true,
      },
      state: {
        type: "selection",
        readOnly: true,
        translate: true,
        default: { id: "draft", name: "draft" },
        options: STATES,
      },
      employee: {
        type: "many2one",
        model: "company.employee",
        readOnly: true,
        required: true,
      },
      section: {
        type: "many2one",
        model: "staff.section",
        required: true,
        preloaded: true,
        readOnly: true,
      },
      attendance: {
        type: "one2many",
        model: "staff.access",
        layout: layoutAttendance(),
      },
      permits_checklist: {
        type: "custom_component",
        Component: PermissionsChecklist,
      },
      event_category: {
        type: "many2one",
        model: "staff.event_category",
        readOnly: readOnlyCommon,
        preloaded: true,
      },
      enter_permission: {
        type: "selection",
        options: OPTIONS,
        readOnly: readOnlyCommon,
      },
      exit_permission: {
        type: "selection",
        options: OPTIONS,
        readOnly: readOnlyCommon,
      },
      total_time: {
        type: "float",
        method: getTotalTime,
        readOnly: true,
      },
      enter_timestamp: {
        type: "datetime",
        readOnly: true,
      },
      exit_timestamp: {
        type: "datetime",
        readOnly: true,
      },
      day_to_compensate: {
        type: "many2one",
        model: "staff.access",
        searchable: true,
        invisible: invisibleDayToCompensate,
        recSearch: (value, rec, parentRec) => {
          const employeeId = rec?.employee?.id || rec?.employee;
          return [
            ["compensatory_date", "!=", null],
            ["compensated", "=", null],
            ["employee", "=", employeeId],
          ];
        },
      },
      // comment: { type: "text" },
    },
    list: [
      { name: "employee" },
      { name: "shift_date" },
      { name: "shift_kind" },
      { name: "notes" },
    ],
    form: [
      { name: "employee" },
      { name: "shift_date" },
      { name: "shift_kind" },
      { name: "section" },
      { name: "enter_timestamp" },
      { name: "exit_timestamp" },
      { name: "total_time" },
      { name: "enter_permission" },
      { name: "exit_permission" },
      { name: "event_category" },
      { name: "day_to_compensate" },
      { name: "state" },
      { name: "notes"},
      { name: "permits_checklist", colSpan: "col-span-2" },

      {
        name: "attendance_section",
        type: "grid",
        colSpan: "col-span-2",
        col: "grid-cols-1",
        elements: [{ name: "attendance" }],
      },
    ],
  };

  if (res.staff_access_extratime === "activated") {
    ctxView.fields.schedule_het = {
      type: "float",
      decimalPlaces: 2,
      readOnly: (rec) => rec?.state?.id !== "draft",
    };
    // ctxView.fields.het = {
    //   type: "float",
    //   readOnly: true,
    // };
    ctxView.form.splice(7, 0, {
      name: "het_section",
      type: "grid",
      colSpan: "col-span-1",
      col: "grid-cols-2",
      elements: [{ name: "schedule_het" }],
    });
  }
  if (res.staff_access_bill === "activated") {
    ctxView.fields.activity = {
      type: "many2one",
      model: "staff.event_category",
      recSearch: () => [["type_event", "=", "overtime"]],
      searchable: true,
      preloaded: true,
      readOnly: readOnlyCommon,
    };
    ctxView.fields.is_billable = {
      type: "selection",
      options: IS_BILLABLE,
      readOnly: readOnlyCommon,
    };
    ctxView.form.splice(9, 0, { name: "activity" });
    ctxView.form.splice(10, 0, { name: "is_billable" });
    ctxView.list.push({ name: "activity" });
  }
  if (res.crm_staff === "activated") {
    ctxView.fields.position = {
      type: "many2one",
      model: "staff.position",
      searchable: true,
      // recSearch: () => [["type_event", "=", "overtime"]],
      // preloaded: true,
      // readOnly: readOnlyCommon,
    };
    ctxView.form.splice(10, 0, { name: "position" });
    ctxView.fields.shift_amount = {
      type: "numeric",
      readOnly: true,
    };
    ctxView.form.splice(11, 0, { name: "shift_amount" });
  }
  return ctxView;
}

export { layoutShift, IS_BILLABLE };
